import React, { useCallback, useContext } from 'react';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Text } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { HarmonizationResults } from './harmonization-results';
import { harmonizationMessages } from './messages';
import styles from './styles.module.less';
import { useHarmonizationSuggestion } from './use-harmonization-suggestion';
import Global from '../../store/global-selectors';
import { Workflows } from '../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { ProductDetailsContext } from '../product-details/context';

/**
 * @param {{
 *   onCodeSelected: (code: string) => void
 * }} props
 * @returns {React.ReactElement}
 */
export const HarmonizationSuggestion = ({ onCodeSelected }) => {
  const { productId, description, customsDescription, harmonizationWorkflow, setHarmonizationWorkflow } = useContext(ProductDetailsContext);
  const productRecord = useSelector((state) => Global.selectEntitiesProductsById(state, { productId }));
  const { suggestions, isLoading, generateSuggestions, clearSuggestions } = useHarmonizationSuggestion(
    productId,
    description,
    customsDescription,
  );

  const trackHarmonization = useTrackWorkflow(Workflows.HARMONIZATION_SINGLE_FLOW);

  const handleGenerateSuggestions = useCallback(async () => {
    const workflowData = trackHarmonization(WorkflowSteps.START, {
      productId,
    });

    setHarmonizationWorkflow({
      isActive: true,
      selectedCode: null,
      selectedConfidence: null,
    });

    await generateSuggestions(workflowData.workflowGuid);
  }, [trackHarmonization, productId, generateSuggestions, setHarmonizationWorkflow]);

  const handleCodeSelected = useCallback(
    (code) => {
      const suggestion = suggestions.find((s) => s.suggestedCode === code);

      if (suggestion) {
        trackHarmonization(WorkflowSteps.HARMONIZATION_CODE_SELECTED, {
          productId,
          selectedCode: suggestion.suggestedCode,
          confidenceLevel: suggestion.confidenceLevel,
          reasoning: suggestion.reasoning,
        });

        setHarmonizationWorkflow((prev) => ({
          ...prev,
          selectedCode: suggestion.suggestedCode,
          selectedConfidence: suggestion.confidenceLevel,
        }));
      }

      onCodeSelected(code);
    },
    [trackHarmonization, productId, suggestions, onCodeSelected, setHarmonizationWorkflow],
  );

  const handleClearSuggestions = useCallback(() => {
    if (harmonizationWorkflow.isActive) {
      trackHarmonization(WorkflowSteps.CANCEL, {
        selectedProduct: harmonizationWorkflow.selectedCode
          ? {
              productId,
              code: harmonizationWorkflow.selectedCode,
            }
          : {},
      });

      setHarmonizationWorkflow({
        isActive: false,
        selectedCode: null,
        selectedConfidence: null,
      });
    }

    clearSuggestions();
    onCodeSelected(productRecord?.customsTariffNo);
  }, [harmonizationWorkflow, trackHarmonization, productId, clearSuggestions, onCodeSelected, productRecord, setHarmonizationWorkflow]);

  const hasSuggestions = suggestions.length > 0;
  const hasAnyDescription =
    Boolean(description && description.trim().length > 0) || Boolean(customsDescription && customsDescription.trim().length > 0);
  const isButtonDisabled = isLoading || !hasAnyDescription;
  const disabledTooltipMessage = !hasAnyDescription ? (
    <Text value={harmonizationMessages.newProductRequiresCustomsDescription} />
  ) : undefined;

  return (
    <>
      <div className={styles.suggestionContainer}>
        <Button
          className={styles.suggestButton}
          disabled={isButtonDisabled}
          disabledTooltipMessage={disabledTooltipMessage}
          isLoading={isLoading}
          onClick={handleGenerateSuggestions}
          theme={THEME_CONSTANTS.primary}
        >
          <>
            <Text value={harmonizationMessages.suggestWithAI} />
            {!isLoading ? <FontAwesomeIcon icon={faSparkles} /> : null}
          </>
        </Button>
      </div>

      {hasSuggestions ? (
        <HarmonizationResults onClear={handleClearSuggestions} onCodeClick={handleCodeSelected} suggestions={suggestions} />
      ) : null}
    </>
  );
};
